import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, ChevronDown, MessageCircle, Twitter, CheckCircle } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { CreatorInfo } from "@/components/kol/CreatorInfo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import KolTweetsView from "./KolTweetsView";
import { StyledBadge } from "@/components/kol/StyledBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface KolProfile {
  id: string;
  creator_name: string;
  social_media_handler: string;
  social_media_type: string;
  profile_image_url: string | null;
  region: string | null;
  followers: number | null;
  tier: string | null;
  kol_type: string | null;
  followed_by?: string[] | null;
}

export const TwitterKolList = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [twitterKols, setTwitterKols] = useState<KolProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [tierFilter, setTierFilter] = useState<string[]>([]);
  const [followedByFilter, setFollowedByFilter] = useState<string[]>([]);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [availableTiers, setAvailableTiers] = useState<string[]>([]);
  const [availableFollowers, setAvailableFollowers] = useState<string[]>([]);
  const [selectedKol, setSelectedKol] = useState<KolProfile | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  useEffect(() => {
    const fetchTwitterKols = async () => {
      try {
        setIsLoading(true);
        
        console.log('Fetching Twitter KOLs with profile images...');
        
        const { data, error } = await supabase
          .from('kol_db')
          .select('id, creator_name, social_media_handler, social_media_type, profile_image_url, region, followers, tier, kol_type, pic_id')
          .eq('social_media_type', 'Twitter')
          .order('followers', { ascending: false });
        
        if (error) throw error;
        
        console.log(`Fetched ${data?.length || 0} Twitter KOLs`);
        console.log('Sample KOL data:', data?.[0]); // Debug log to see the actual data structure

        // Extract unique regions and tiers for filtering
        const regions = Array.from(new Set(data?.map(kol => kol.region).filter(Boolean) as string[]));
        const tiers = Array.from(new Set(data?.map(kol => kol.tier).filter(Boolean) as string[]));
        
        setAvailableRegions(regions);
        setAvailableTiers(tiers);
        
        // Map database results to KolProfile interface - ensure profile_image_url is properly mapped
        const mappedKols: KolProfile[] = data?.map(kol => {
          console.log(`KOL ${kol.creator_name} profile image:`, kol.profile_image_url); // Debug log
          return {
            id: kol.id,
            creator_name: kol.creator_name,
            social_media_handler: kol.social_media_handler,
            social_media_type: kol.social_media_type,
            profile_image_url: kol.profile_image_url, // Make sure this is correctly mapped
            region: kol.region,
            followers: kol.followers,
            tier: kol.tier,
            kol_type: kol.kol_type,
            followed_by: kol.pic_id as string[] || []
          };
        }) || [];
        
        setTwitterKols(mappedKols);
      } catch (error) {
        console.error("Error fetching Twitter KOLs:", error);
        toast({
          title: "Error",
          description: "Failed to load Twitter KOL list",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTwitterKols();
  }, [toast]);

  const filteredKols = twitterKols.filter(kol => {
    // Apply search filter
    const matchesSearch = 
      kol.creator_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      kol.social_media_handler?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply region filter
    const matchesRegion = regionFilter.length === 0 || 
      (kol.region && regionFilter.includes(kol.region));
    
    // Apply tier filter
    const matchesTier = tierFilter.length === 0 || 
      (kol.tier && tierFilter.includes(kol.tier));
    
    // Handle followedBy as optional
    const matchesFollowedBy = followedByFilter.length === 0 || 
      (kol.followed_by && kol.followed_by.some(follower => followedByFilter.includes(follower)));
    
    return matchesSearch && matchesRegion && matchesTier && matchesFollowedBy;
  });

  const getFollowerCount = (count: number | null) => {
    if (!count) return "Unknown";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };
  
  // Get initials from name
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };
  
  const handleKolClick = (kol: KolProfile) => {
    setSelectedKol(kol);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="text-xl font-semibold">KOL Dictionary</h2>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search KOLs..."
              className="pl-8 w-full md:w-60"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {/* Region filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border ${isLightMode ? 'bg-white hover:bg-gray-50' : 'bg-background hover:bg-secondary/80'}`}>
                <Filter className="h-3.5 w-3.5" />
                Region
                {regionFilter.length > 0 && (
                  <Badge variant="secondary" className="ml-1 font-normal text-[10px] py-0 px-1.5">
                    {regionFilter.length}
                  </Badge>
                )}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {availableRegions.map(region => (
                <DropdownMenuCheckboxItem
                  key={region}
                  checked={regionFilter.includes(region)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setRegionFilter([...regionFilter, region]);
                    } else {
                      setRegionFilter(regionFilter.filter(r => r !== region));
                    }
                  }}
                >
                  {region}
                </DropdownMenuCheckboxItem>
              ))}
              {availableRegions.length === 0 && (
                <div className="p-2 text-sm text-muted-foreground">No regions available</div>
              )}
              {regionFilter.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="p-2">
                    <button 
                      className="text-xs text-primary w-full text-center"
                      onClick={() => setRegionFilter([])}
                    >
                      Clear filters
                    </button>
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Tier filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border ${isLightMode ? 'bg-white hover:bg-gray-50' : 'bg-background hover:bg-secondary/80'}`}>
                <Filter className="h-3.5 w-3.5" />
                Tier
                {tierFilter.length > 0 && (
                  <Badge variant="secondary" className="ml-1 font-normal text-[10px] py-0 px-1.5">
                    {tierFilter.length}
                  </Badge>
                )}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {availableTiers.map(tier => (
                <DropdownMenuCheckboxItem
                  key={tier}
                  checked={tierFilter.includes(tier)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setTierFilter([...tierFilter, tier]);
                    } else {
                      setTierFilter(tierFilter.filter(t => t !== tier));
                    }
                  }}
                >
                  {tier}
                </DropdownMenuCheckboxItem>
              ))}
              {tierFilter.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="p-2">
                    <button 
                      className="text-xs text-primary w-full text-center"
                      onClick={() => setTierFilter([])}
                    >
                      Clear filters
                    </button>
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isLoading ? (
        <TableLoadingOverlay isLoading={isLoading} message="Loading Twitter KOLs..." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredKols.length === 0 ? (
            <div className="col-span-full flex justify-center items-center py-12">
              <p className="text-muted-foreground">No KOLs found</p>
            </div>
          ) : (
            filteredKols.map((kol) => {
              console.log(`Rendering KOL ${kol.creator_name} with profile image:`, kol.profile_image_url); // Debug log
              return (
                <Card 
                  key={kol.id} 
                  className={`overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${isLightMode ? 'bg-white' : 'bg-card border-border'}`}
                  onClick={() => handleKolClick(kol)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-3 flex-1">
                        {/* Use CreatorInfo component with proper profile image */}
                        <div className="flex-1">
                          <CreatorInfo 
                            name={kol.creator_name}
                            handler={kol.social_media_handler}
                            platform={kol.social_media_type}
                            region={kol.region}
                            profileImageUrl={kol.profile_image_url} // This should now correctly use the profile image from kol_db
                            compact={true}
                          />
                        </div>
                      </div>
                      
                      {/* Prominent follower count */}
                      {kol.followers && (
                        <div className="text-right">
                          <div className="font-bold text-lg text-primary">
                            {getFollowerCount(kol.followers)}
                          </div>
                          <div className="text-xs text-muted-foreground">followers</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Badges area with smaller badges */}
                    <div className="mt-3 pt-3 border-t border-border/30">
                      <div className="flex flex-wrap gap-1.5">
                        {kol.region && (
                          <StyledBadge 
                            value={kol.region} 
                            variant="blue" 
                            extraSmall={true}
                          />
                        )}
                        {kol.tier && (
                          <StyledBadge 
                            value={`Tier ${kol.tier}`} 
                            variant="primary" 
                            extraSmall={true}
                          />
                        )}
                        {kol.social_media_type && (
                          <StyledBadge 
                            value={kol.social_media_type} 
                            variant="outline" 
                            extraSmall={true}
                          />
                        )}
                        {kol.kol_type && (
                          <StyledBadge 
                            value={kol.kol_type} 
                            variant="dark" 
                            extraSmall={true}
                          />
                        )}
                        <div className="ml-auto flex items-center">
                          <MessageCircle className="h-3 w-3 text-muted-foreground mr-1" />
                          <span className="text-xs text-muted-foreground">View Tweets</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
      
      {/* KOL Tweets Dialog */}
      {selectedKol && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-5xl w-[90vw]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedKol.profile_image_url && (
                  <img 
                    src={selectedKol.profile_image_url} 
                    alt={selectedKol.creator_name} 
                    className="w-6 h-6 rounded-full" 
                  />
                )}
                {selectedKol.creator_name} (@{selectedKol.social_media_handler})
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[75vh] overflow-y-auto pt-4">
              <KolTweetsView 
                kolId={selectedKol.id} 
                username={selectedKol.social_media_handler} 
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
  
  // Helper function to get social media icon
  function getSocialMediaIcon(type: string) {
    switch(type.toLowerCase()) {
      case 'twitter':
      case 'x':
        return <Twitter className="h-4 w-4 text-[#1DA1F2] mr-1" />;
      default:
        return null;
    }
  }
};

export default TwitterKolList;
